import {
  FilterKeys,
  GetSignUpsListQueryFilters,
  SchoolYear,
  SignUpStatuses,
  SignUpType,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  DropdownSelectFilter,
  EventsParticipationOptions,
  FiltersSidebar,
  FiltersSidebarMethods,
  getParticipantLabel,
  getSignUpsStatusLabel,
  getSignUpsTypeLabel,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
  SignUpsStatusOptions,
  SignUpsTypeOptions,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
} from '@schooly/components/filters';
import { EventsInvite } from '@schooly/constants';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSignUpsFilters } from './useSignUpsFilters';

type SignUpsFiltersSidebarProps = {
  filters: GetSignUpsListQueryFilters;
  defaultFilters: GetSignUpsListQueryFilters;
  schoolId: string;
  onApply: (v: GetSignUpsListQueryFilters) => void;
  defaultSchoolYear?: SchoolYear;
};

export const SignUpsFiltersSidebar = forwardRef<FiltersSidebarMethods, SignUpsFiltersSidebarProps>(
  ({ filters: propsFilters, defaultFilters, onApply, schoolId, defaultSchoolYear }, ref) => {
    const [filters, setFilters] = useState(propsFilters);
    const { formatMessage } = useIntl();
    const { permissions } = useAuth();
    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
    const periodsRef = useRef<SchoolPeriodsRangeSelectComponent>(null);
    const {
      labels,
      filtersSignUpStatus,
      filtersSignUpType,
      filtersInviteType,
      onSetDate,
      onSetSignUpStatus,
      onClearSignUpStatus,
      onSetSignUpType,
      onClearSignUpType,
      onSetInviteType,
      onClearInviteType,
    } = useSignUpsFilters({ filters, onSetFilters: setFilters });

    const onSelectSignUpStatus = useCallback(
      (status: SignUpStatuses) => {
        onSetSignUpStatus(toggleMultipleValueArrayProperty(filtersSignUpStatus, [status]));
      },
      [filtersSignUpStatus, onSetSignUpStatus],
    );

    const onSelectSignUpType = useCallback(
      (type: SignUpType) => {
        onSetSignUpType(toggleMultipleValueArrayProperty(filtersSignUpType, [type]));
      },
      [filtersSignUpType, onSetSignUpType],
    );

    const onSelectInviteType = useCallback(
      (inviteType: EventsInvite) => {
        onSetInviteType(toggleMultipleValueArrayProperty(filtersInviteType, [inviteType]));
      },
      [filtersInviteType, onSetInviteType],
    );

    const handleApply = useCallback(() => {
      onApply(filters);
    }, [filters, onApply]);

    const handleClear = useCallback(() => {
      setFilters(defaultFilters);
      if (defaultFilters.date) {
        periodsRef.current?.updateDates(defaultFilters.date);
      }
    }, [defaultFilters]);

    return (
      <FiltersSidebar
        onOpen={() => {
          setFilters(propsFilters);
        }}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-SignUps' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <SchoolPeriodsRangeSelect
          ref={periodsRef}
          showSelectedPeriodLabel={false}
          schoolId={schoolId}
          defaultSchoolYear={defaultSchoolYear}
          hasManagePermission={permissions.includes('school_admin')}
          onSetDate={onSetDate}
          placeholder={labels[FilterKeys.Date]}
          date={filters.date || []}
          requiredLabel="required"
          onOpen={preventClose}
          onClose={allowClose}
          schoolYearFromToday
        />

        <DropdownSelectFilter
          placeholder={labels[FilterKeys.SignUpStatus]}
          onSelect={onSelectSignUpStatus}
          selected={filtersSignUpStatus || []}
          options={SignUpsStatusOptions}
          getLabel={(status) => formatMessage({ id: getSignUpsStatusLabel(status) })}
          onClear={onClearSignUpStatus}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <DropdownSelectFilter
          placeholder={labels[FilterKeys.SignUpType]}
          onSelect={onSelectSignUpType}
          selected={filtersSignUpType || []}
          options={SignUpsTypeOptions}
          getLabel={(type) => formatMessage({ id: getSignUpsTypeLabel(type) })}
          onClear={onClearSignUpType}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <DropdownSelectFilter
          placeholder={labels[FilterKeys.InviteType]}
          onSelect={onSelectInviteType}
          selected={filtersInviteType || []}
          options={EventsParticipationOptions}
          getLabel={(inviteType) => formatMessage({ id: getParticipantLabel(inviteType) })}
          onClear={onClearInviteType}
          onOpen={preventClose}
          onClose={allowClose}
        />
      </FiltersSidebar>
    );
  },
);
